import { createContext, FC, PropsWithChildren, useContext, useMemo, useRef } from "react";

import { DynamicFormV2Palette } from "@mui/material";
import { hslToHex } from "@simplyk/common";

import { hexToHue } from "@/canvas/components/BackgroundAnimations/hexToHue";
import { mapFormThemeToCanvasBackgroundTheme } from "@/canvas/components/BackgroundAnimations/mapFormThemeToCanvasBackgroundTheme";
import { Burst, BurstFromClientRectOptions, BurstHandle, BurstType } from "@/canvas/components/Burst/Burst";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";

const mapFormThemeToBurstColor = ({
  formsPalette,
  mode,
}: {
  formsPalette: DynamicFormV2Palette["forms"];
  mode: "light" | "dark";
}): string => hslToHex(mode === "dark" ? formsPalette.surface.reverse : formsPalette.surface.moderate);

export type BurstFromElementOptions = Pick<BurstFromClientRectOptions, "type" | "align">;

export interface BurstContextType {
  burstFromElement: (element: HTMLElement | null, options?: BurstFromElementOptions) => void;
}

const noop = () => {};

export const BurstContext = createContext<BurstContextType>({
  burstFromElement: noop,
});

export const useBurst = (): BurstContextType => useContext(BurstContext);

interface BurstProviderProps {
  type: BurstType | "none";
  brandColorHex?: string;
  canBurstHeart?: boolean;
}

// Cypress drives visual-regression snapshots; in-flight burst particles make them flaky.
const isCypressTest = typeof window !== "undefined" && Boolean((window as Window & { Cypress?: unknown }).Cypress);

export const BurstProvider: FC<PropsWithChildren<BurstProviderProps>> = ({
  children,
  type,
  brandColorHex,
  canBurstHeart = false,
}) => {
  const burstRef = useRef<BurstHandle>(null);
  const canvasTheme = useDynamicThemeAwareVariables(mapFormThemeToCanvasBackgroundTheme);
  const burstColor = useDynamicThemeAwareVariables(mapFormThemeToBurstColor);
  const hue = useMemo(() => (brandColorHex ? hexToHue(brandColorHex) : 0), [brandColorHex]);

  const contextValue = useMemo<BurstContextType>(
    () => ({
      burstFromElement: (element, options) => {
        if (isCypressTest) {
          return;
        }
        if (!element || typeof element.getBoundingClientRect !== "function") {
          return;
        }
        burstRef.current?.burstFromClientRect(element.getBoundingClientRect(), options);
      },
    }),
    []
  );

  const shouldMountBurst = (type !== "none" || canBurstHeart) && !isCypressTest;

  return (
    <BurstContext.Provider value={contextValue}>
      {children}
      {shouldMountBurst && <Burst ref={burstRef} type={type} hue={hue} theme={canvasTheme} color={burstColor} />}
    </BurstContext.Provider>
  );
};
